import { useNavigate, useParams } from "react-router-dom";
import {
  useContact,
  useUpdateContact,
  useDeleteContact,
} from "@/hooks/use-contacts";
import { useToast } from "@/contexts";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { ArrowLeft, Trash2 } from "lucide-react";
import {
  ContactForm,
  ContactFormData,
} from "../../components/organisms/ContactForm";

export function ContactEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: contact, isLoading, error } = useContact(id!);
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();
  const { showError, showSuccess } = useToast();

  const handleSubmit = async (data: ContactFormData) => {
    try {
      // Convert null to undefined for API compatibility
      const body = {
        ...data,
        priceMin: data.priceMin ?? undefined,
        priceMax: data.priceMax ?? undefined,
      };
      await updateContact.mutateAsync({
        params: { id: id! },
        body,
      });
      showSuccess("Contact updated successfully");
      navigate("/contacts");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update contact";
      showError(message);
    }
  };

  const handleCancel = () => {
    navigate("/contacts");
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      try {
        await deleteContact.mutateAsync({ params: { id: id! } });
        showSuccess("Contact deleted");
        navigate("/contacts");
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to delete contact";
        showError(message);
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 256,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contact) {
    return <Alert severity="error">Failed to load contact</Alert>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowLeft size={20} />}
        onClick={() => navigate("/contacts")}
        sx={{ mb: 3 }}
      >
        Back to Contacts
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Edit Contact
        </Typography>
        <Button
          color="error"
          startIcon={<Trash2 size={18} />}
          onClick={handleDelete}
          disabled={deleteContact.isPending}
        >
          {deleteContact.isPending ? "Deleting..." : "Delete"}
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <ContactForm
          key={contact.id}
          contact={{
            id: contact.id,
            name: contact.name,
            email: contact.email,
            emailRecipients: contact.emailRecipients,
            suburb: contact.suburb,
            suburbSecondary: contact.suburbSecondary,
            priceMin: contact.priceMin,
            priceMax: contact.priceMax,
            propertyTypes: contact.propertyTypes,
            reportFrequency: contact.reportFrequency,
            isActive: contact.isActive,
          }}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={updateContact.isPending}
        />
      </Paper>
    </Box>
  );
}
